// === src/pages/AdminList.js (horizontal scroll enabled) ===
import React, { useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatPhoneInput } from '../utils/phone';
import PhoneInput from '../components/PhoneInput';

axios.defaults.withCredentials = true;

const BASE_LEVELS   = ['一般顧客','年會員','月會員'];
const FILTER_LEVELS = ['一般顧客','年會員','資深年會員','月會員'];
const getDisplayLevel = (level, pointsEarned) =>
  (level === '年會員' && (pointsEarned || 0) >= 20) ? '資深年會員' : (level || '一般顧客');

export default function AdminList() {
  const navigate = useNavigate();
  const location = useLocation();
  const homePath = location.pathname.startsWith('/count') ? '/count' : '/admin';

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    fName:'', fPhone:'', fEmail:'', fLevel:'', fBirthMonth:'', fAbsentDays:''
  });

  const PAGE_SIZE = 100;
  const [page, setPage] = useState(1);

  // 新增會員用
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    NAME: '', PHONE: '', EMAIL: '', LEVEL: '一般顧客', BIRTHDAY: ''
  });
  const [busyCreate, setBusyCreate] = useState(false);

  const { fName, fPhone, fEmail, fLevel, fBirthMonth, fAbsentDays } = filters;
  const fetchMembers = async (nextPage = 1) => {
    const params = new URLSearchParams({ paged: '1', page: String(nextPage) });
    if (fName.trim()) params.set('name', fName.trim());
    if (fPhone.trim()) params.set('phone', fPhone.trim());
    if (fEmail.trim()) params.set('email', fEmail.trim());
    if (fLevel) params.set('level', fLevel);
    if (fBirthMonth) params.set('birthMonth', fBirthMonth);
    if (String(fAbsentDays).trim() !== '') params.set('absentDays', String(fAbsentDays).trim());
    if (![...params.keys()].some((k) => !['paged', 'page'].includes(k))) {
      alert('請先輸入搜尋條件');
      return;
    }
    try {
      setLoading(true);
      setSearched(true);
      const { data } = await axios.get(`/api/admin/members?${params}`);
      setMembers(Array.isArray(data?.data) ? data.data : []);
      setTotal(Number(data?.total) || 0);
      setPage(Number(data?.page) || nextPage);
    } catch {
      setMembers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  // 只帶 phone，沒有電話就給提示
  const goEditOnSearch = (m) => {
    const phone = (m?.PHONE || '').trim();
    if (!phone) {
      alert('這筆沒有電話，無法帶入搜尋。');
      return;
    }
    navigate(`${homePath}?phone=${encodeURIComponent(phone)}`);
  };

  // 新增會員
  const createMember = async (e) => {
    e?.preventDefault?.();
    if (!createForm.NAME.trim())  return alert('請輸入姓名');
    if (!createForm.PHONE.trim()) return alert('請輸入電話');
    try {
      setBusyCreate(true);
      const body = {
        NAME: createForm.NAME.trim(),
        PHONE: createForm.PHONE.trim(), // 移除格式限制：直接使用原始輸入
        EMAIL: createForm.EMAIL.trim(),
        LEVEL: createForm.LEVEL || '一般顧客',
        BIRTHDAY: createForm.BIRTHDAY || undefined,
      };
      await axios.post('/api/admin/member', body);
      setShowCreate(false);
      setCreateForm({ NAME:'', PHONE:'', EMAIL:'', LEVEL:'一般顧客', BIRTHDAY:'' });
      await fetchMembers(page);
    } catch (e) {
      const errorMsg = e?.response?.data?.message || '新增失敗，請稍後再試';
      alert(errorMsg);
    } finally {
      setBusyCreate(false);
    }
  };

  // 樣式 & 工具
  const isExpired = (m) => {
    const s = m?.MEMBER_EXPIRY;
    if (!s) return false;
    const d = new Date(s);
    if (isNaN(d)) return false;
    const today = new Date();
    const ymd = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    return ymd(d) < ymd(today);
  };

  const monthOptions = Array.from({length:12}, (_,i)=>i+1);

  // 外層卷動容器 + 內層寬度容器
  const pageWrap = {
    maxWidth: 1200,
    margin: '0 auto',
    padding: 12,
    fontSize: 16,
    lineHeight: 1.35
  };
  const filterInput = { flex:1, padding:'10px 12px', fontSize:16, border:'1px solid #ccc', borderRadius:8 };

  return (
    <div className="admin-list-scroll">
      {/* 本頁滾動＋排版安全樣式（啟用左右滑動） */}
      <style>{`
        .admin-list-scroll {
          height: 100%;
          min-height: 0;
          overflow-y: auto;
          overflow-x: auto;                 /* ← 允許水平捲動 */
          -webkit-overflow-scrolling: touch;/* ← 手機慣性滑動 */
          overscroll-behavior-x: contain;   /* ← 防止橫向滑出頁面 */
          scrollbar-gutter: stable both-edges;
          box-sizing: border-box;
        }
        .admin-list-scroll table {
          width: 100%;
          max-width: none;                  /* ← 不限制最大寬，讓超出時可橫向捲動 */
          table-layout: fixed;
        }
        .admin-list-scroll th, .admin-list-scroll td {
          word-break: break-word;
          overflow-wrap: anywhere;
          white-space: nowrap;              /* ← 保持一行，避免擠爆 */
          text-overflow: ellipsis;
        }
        .admin-list-toolbar {
          position: sticky;
          top: 0;
          z-index: 5;
          background: #fff;
          padding-top: 4px;
          margin-top: -4px;
          border-bottom: 1px solid #eee;
        }
        @media (max-width: 1100px) {
          .admin-list-toolbar .filters { min-width: 0; }
        }
      `}</style>

      <div className="wrap" style={pageWrap}>
        {/* 功能列：新增、篩選（sticky） */}
        <div className="admin-list-toolbar">
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12, flexWrap:'wrap' }}>
            <button onClick={()=>setShowCreate(v=>!v)} style={{ padding:'10px 12px', fontSize:16, borderRadius:8 }}>
              {showCreate ? '關閉新增' : '新增會員'}
            </button>

            <div className="filters" style={{ display:'flex', gap:8, flex:1, minWidth:520 }}>
              <input placeholder="姓名"  value={fName}  onChange={e=>setFilters(f => ({...f, fName:  e.target.value}))} style={filterInput}/>
              <input
                type="tel"
                inputMode="numeric"
                placeholder="電話（搜尋）"
                value={fPhone}
                onChange={e=>setFilters(f => ({...f, fPhone: e.target.value}))}
                style={filterInput}
              />
              <button type="button" onClick={() => setFilters((f) => {
                const raw = String(f.fPhone || '').trim();
                if (raw.startsWith('+886')) return f;
                const digits = raw.replace(/[^\d]/g, '').replace(/^886/, '').replace(/^0+/, '');
                return { ...f, fPhone: `+886${digits}` };
              })} style={{ padding:'10px 12px', borderRadius:8 }}>+886</button>
              <input placeholder="信箱"  value={fEmail} onChange={e=>setFilters(f => ({...f, fEmail: e.target.value}))} style={filterInput}/>
              <select value={fLevel} onChange={e=>setFilters(f => ({...f, fLevel: e.target.value}))} style={{ ...filterInput, padding:'10px', minWidth:140 }}>
                <option value="">全部等級</option>
                {FILTER_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>

              {/* 生日月份 */}
              <select value={fBirthMonth} onChange={e=>setFilters(f => ({...f, fBirthMonth: e.target.value}))} style={{ ...filterInput, padding:'10px', minWidth:140 }} title="按生日月份篩選">
                <option value="">全部月份</option>
                {monthOptions.map(m => <option key={m} value={m}>{m} 月生日</option>)}
              </select>

              {/* ★ 幾天沒來 */}
              <input type="number" min={0} placeholder="≥ 幾天沒來" value={fAbsentDays} onChange={e=>setFilters(f => ({...f, fAbsentDays: e.target.value}))} style={{ ...filterInput, maxWidth: 160 }} title="依最後入場距今天數篩選" />
              <button type="button" onClick={() => fetchMembers(1)} style={{ padding:'10px 14px', fontSize:16, borderRadius:8 }}>搜尋</button>
            </div>
          </div>

          {/* 新增表單 */}
          {showCreate && (
            <form onSubmit={createMember} style={{ marginBottom:16, padding:12, border:'1px solid #eee', borderRadius:12, background:'#fff' }}>
              <h3 style={{ margin:'0 0 10px', fontSize:18 }}>新增會員</h3>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:10 }}>
                <div>
                  <div style={{ fontSize:14, color:'#555', marginBottom:4 }}>姓名</div>
                  <input placeholder="姓名" value={createForm.NAME} onChange={e=>setCreateForm(f=>({...f, NAME:e.target.value}))} style={{ ...filterInput, width:'100%' }} />
                </div>
                <div>
                  <div style={{ fontSize:14, color:'#555', marginBottom:4 }}>電話</div>
                  <PhoneInput
                    value={createForm.PHONE}
                    onChange={(val)=>setCreateForm(f=>({...f, PHONE: val}))}
                    placeholder="例：912345678"
                    selectStyle={{ padding: '8px 4px', fontSize: 14, minWidth: 90, borderRadius: '8px 0 0 8px' }}
                    inputStyle={{ padding: '8px 10px', fontSize: 14, borderRadius: '0 8px 8px 0' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize:14, color:'#555', marginBottom:4 }}>信箱（可留空）</div>
                  <input placeholder="email@example.com" value={createForm.EMAIL} onChange={e=>setCreateForm(f=>({...f, EMAIL:e.target.value}))} style={{ ...filterInput, width:'100%' }} />
                </div>
                <div>
                  <div style={{ fontSize:14, color:'#555', marginBottom:4 }}>等級</div>
                  <select value={createForm.LEVEL} onChange={e=>setCreateForm(f=>({...f, LEVEL:e.target.value}))} style={{ ...filterInput, width:'100%', padding:'10px' }}>
                    {BASE_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize:14, color:'#555', marginBottom:4 }}>生日（YYYY-MM-DD）</div>
                  <input type="date" placeholder="生日" value={createForm.BIRTHDAY} onChange={e=>setCreateForm(f=>({...f, BIRTHDAY:e.target.value}))} style={{ ...filterInput, width:'100%' }} />
                </div>
              </div>
              <div style={{ marginTop:12 }}>
                <button type="submit" disabled={busyCreate} style={{ padding:'10px 16px', fontSize:16, borderRadius:8 }}>
                  {busyCreate ? '新增中…' : '送出新增'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* 會員列表（僅瀏覽 + 前往編輯） */}
        {!searched ? (
          <p style={{ fontSize:16, textAlign:'left' }}>請輸入條件後搜尋</p>
        ) : loading ? (
          <p style={{ fontSize:16, textAlign:'left' }}>載入中…</p>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed', fontSize:16 }}>
            <colgroup>
              <col style={{ width: 70 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 220 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 170 }} /> {/* 上次入場 */}
              <col style={{ width: 90 }} />  {/* 距今天數 */}
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr>
                {['ID','姓名','電話','信箱','等級','到期','生日','點數','上次入場','距今(天)',''].map(h => (
                  <th key={h} style={{ padding:'10px 10px', borderBottom:'1px solid #eee', textAlign:'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map(m => {
                const displayLevel = m.DISPLAY_LEVEL || getDisplayLevel(m.LEVEL, m.POINTS_EARNED || 0);
                const senior = displayLevel === '資深年會員';
                const rowStyle = isExpired(m) ? {background:'#fffae6',color:'#b45309'} : undefined;
                const td = { padding:'10px 10px', borderBottom:'1px solid #f3f3f3', textAlign:'left', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' };
                const last = m.LAST_ENTRANCE ? String(m.LAST_ENTRANCE).replace('T',' ').slice(0,19) : '—';
                const days = (m.DAYS_SINCE ?? '') === '' ? '—' : m.DAYS_SINCE;
                return (
                  <tr key={m.ID} style={rowStyle}>
                    <td style={td}>{m.ID}</td>
                    <td style={td} title={m.NAME || ''}>{m.NAME || ''}</td>
                    <td style={td} title={m.PHONE || ''}>{m.PHONE || ''}</td>
                    <td style={td} title={m.EMAIL || ''}>{m.EMAIL || ''}</td>
                    <td style={td}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span>{displayLevel}</span>
                        {senior && (
                          <span style={{ fontSize:12, color:'#b45309', background:'#fff7ed', padding:'2px 6px', borderRadius:6, border:'1px solid #fde68a' }}>
                            資深
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={td}>{(m.MEMBER_EXPIRY?.split?.('T')?.[0]) || ''}</td>
                    <td style={td}>{(m.BIRTHDAY?.split?.('T')?.[0]) || (m.BIRTHDAY || '')}</td>
                    <td style={td}>{m.POINTS || 0}</td>
                    <td style={td}>{last}</td>
                    <td style={{...td, textAlign:'center'}}>{days}</td>
                    <td style={td}>
                      <button onClick={()=>goEditOnSearch(m)} style={{ padding:'8px 10px', fontSize:15, borderRadius:8 }}>
                        前往編輯
                      </button>
                    </td>
                  </tr>
                );
              })}
              {members.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ padding:'12px', textAlign:'left', color:'#666' }}>
                    沒有符合條件的會員
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {!loading && searched && total > 0 && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'12px 4px', flexWrap:'wrap' }}>
            <span style={{ color:'#666', fontSize:14 }}>
              共 {total} 筆，第 {safePage}/{pageCount} 頁（每頁 {PAGE_SIZE} 筆）
            </span>
            <div style={{ display:'flex', gap:8 }}>
              <button
                onClick={() => fetchMembers(Math.max(1, safePage - 1))}
                disabled={safePage <= 1}
                style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #d1d5db', background: safePage<=1 ? '#f3f4f6' : '#fff', cursor: safePage<=1 ? 'not-allowed' : 'pointer' }}
              >
                上一頁
              </button>
              <button
                onClick={() => fetchMembers(Math.min(pageCount, safePage + 1))}
                disabled={safePage >= pageCount}
                style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #d1d5db', background: safePage>=pageCount ? '#f3f4f6' : '#fff', cursor: safePage>=pageCount ? 'not-allowed' : 'pointer' }}
              >
                下一頁
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
